import { Form, Link, useSearchParams, useLoaderData } from "@remix-run/react";
import type { LoaderFunctionArgs } from "@remix-run/node";
import {
  AuthButton,
  AuthField,
  AuthInput,
  AuthLink,
  AuthNote,
  AuthPassword,
  AuthShell,
} from "~app/components/v13/auth";
import { authClient } from "~app/lib/auth-client";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@bprogress/remix";
import { ROUTES } from "~server/routes";
import { handleAuthLoader } from "~app/utils/auth-helpers";
import { signUpErrorMessage } from "~app/utils/auth-messages";
import { getInvitationTypeFromUrl } from "~shared/constants";
import { InvitationType } from "~shared/types/invitation";
import { verifyEmailPath } from "~shared/utils/safe-next";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return handleAuthLoader({ request });
};

const createRegisterSchema = (hasInvitation: boolean) =>
  z
    .object({
      email: hasInvitation
        ? z.string().optional()
        : z.string().email("Please enter a valid email address"),
      password: z.string().min(8, "Password must be at least 8 characters"),
      confirmPassword: z.string(),
      name: z.string().min(1, "Name is required"),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: "Passwords don't match",
      path: ["confirmPassword"],
    });

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { invitation, invitationError } = useLoaderData<typeof loader>();

  const getInvitationErrorMessage = (
    error?: typeof invitationError
  ): string | null => {
    if (!error) return null;
    if (error === "expired") return "This invitation has expired";
    if (error === "already_processed")
      return "This invitation has already been processed";
    return "This invitation is invalid";
  };

  const invitationErrorMessage = getInvitationErrorMessage(invitationError);
  const invitationId = searchParams.get("invitationId");
  const typeParam = searchParams.get("type");

  // Determine invitation type from query param or URL
  const invitationType = invitationId
    ? (typeParam as InvitationType) ||
      (typeof window !== "undefined"
        ? getInvitationTypeFromUrl(window.location.href)
        : null) ||
      InvitationType.ORGANIZATION
    : null;

  const registerSchema = createRegisterSchema(!!invitation?.email);

  const {
    formState: { errors, isSubmitting },
    setError,
    register,
    handleSubmit,
  } = useForm<z.infer<typeof registerSchema>>({
    defaultValues: {
      email: invitation?.email || "",
      password: "",
      confirmPassword: "",
      name: "",
    },
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(registerSchema),
  });

  const signUp = async (data: z.infer<typeof registerSchema>) => {
    // Use invitation email if available, otherwise use form email
    const email = invitation?.email || data.email;

    if (!email) {
      setError("root", { message: "Email is required" });
      return;
    }

    if (invitationErrorMessage) {
      setError("root", { message: invitationErrorMessage });
      return;
    }

    // ⭐ WHERE THE CONFIRMATION LINK LEADS (signup journeys A and B): back to the confirmation
    // page, which then sends the person on — to the invitation they came for, or to setting up
    // their own company. (A super-admin invitation keeps its own, existing flow.)
    const isSuperAdminInvite =
      invitationId && invitationType === InvitationType.SUPER_ADMIN;
    const afterConfirm = isSuperAdminInvite
      ? null
      : invitationId
        ? `/accept-invitation/${invitationId}`
        : ROUTES.START;

    await authClient.signUp.email(
      {
        name: data.name,
        email: email,
        password: data.password,
        callbackURL: verifyEmailPath(afterConfirm),
      },
      {
        onSuccess: () => {
          if (afterConfirm) {
            navigate(verifyEmailPath(afterConfirm));
            return;
          }
          if (invitationId) {
            // Route to appropriate invitation acceptance page based on type
            if (invitationType === InvitationType.SUPER_ADMIN) {
              navigate(`/super-admin/accept-invitation/${invitationId}`);
            } else {
              navigate(`/accept-invitation/${invitationId}`);
            }
            return;
          }
          navigate(ROUTES.HOME);
        },
        onError: (error) => {
          // never the library's own wording (see signUpErrorMessage)
          const { message, accountExists } = signUpErrorMessage(error.error);
          setError("root", {
            type: accountExists ? "account-exists" : "server",
            message,
          });
        },
      }
    );
  };
  return (
    <AuthShell
      title="Welcome to JumpCannon"
      sub={
        invitation?.email
          ? `You're invited. Create your account with ${invitation.email}, then verify your email to continue.`
          : "A clearer view of your foodservice business starts here. Create your account, then verify your email to get started."
      }
      foot={
        <span
          className="text-[var(--yl-muted)]"
          style={{ fontSize: "var(--yl-fs-body)" }}
        >
          Already have an account?{" "}
          <Link
            prefetch="intent"
            to={
              invitationId
                ? {
                    pathname: ROUTES.LOGIN,
                    search: `?invitationId=${invitationId}&type=${invitationType}`,
                  }
                : { pathname: ROUTES.LOGIN }
            }
          >
            <AuthLink>Sign in</AuthLink>
          </Link>
        </span>
      }
    >
      <Form onSubmit={handleSubmit(signUp)} noValidate>
        {errors.root ? (
          <AuthNote>
            {errors.root.message}
            {errors.root.type === "account-exists" ? (
              <>
                {" "}
                <Link prefetch="intent" to={ROUTES.LOGIN}>
                  <AuthLink>Sign in</AuthLink>
                </Link>
                {" · "}
                <Link prefetch="intent" to={ROUTES.FORGOT_PASSWORD}>
                  <AuthLink>Reset your password</AuthLink>
                </Link>
              </>
            ) : null}
          </AuthNote>
        ) : null}

        {invitationError ? (
          <AuthNote>
            {invitationError === "expired"
              ? "That invitation has expired. Ask whoever invited you to send a new one."
              : invitationError === "already_processed"
                ? "That invitation has already been used."
                : "That invitation is not valid."}
          </AuthNote>
        ) : null}

        <AuthField label="Your name" error={errors.name?.message ?? null}>
          <AuthInput
            id="name"
            autoComplete="name"
            placeholder="Jane Doe"
            invalid={Boolean(errors.name)}
            {...register("name")}
          />
        </AuthField>

        <AuthField
          label="Email address"
          error={errors.email?.message ?? null}
          hint={invitation?.email ? "Filled in from your invitation." : null}
        >
          <AuthInput
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            invalid={Boolean(errors.email)}
            readOnly={!!invitation?.email}
            defaultValue={invitation?.email ?? undefined}
            {...register("email")}
          />
        </AuthField>

        <AuthField
          label="Password"
          error={errors.password?.message ?? null}
          hint="At least 8 characters."
        >
          <AuthPassword
            id="password"
            autoComplete="new-password"
            placeholder="Create a password"
            invalid={Boolean(errors.password)}
            {...register("password")}
          />
        </AuthField>

        <AuthField
          label="Confirm password"
          error={errors.confirmPassword?.message ?? null}
        >
          <AuthPassword
            id="confirmPassword"
            autoComplete="new-password"
            placeholder="Enter your password again"
            invalid={Boolean(errors.confirmPassword)}
            {...register("confirmPassword")}
          />
        </AuthField>

        <AuthButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Creating your account…" : "Create account"}
        </AuthButton>
      </Form>
    </AuthShell>
  );
}
